import { ReactElement, ReactNode } from 'react';
import classNames from 'classnames/bind';
import { Chip, ColourVariant, FormatInformation, SaladIcon, Spinner, Typography } from '@weareopenr/component-library';
import { useAuthWrapper } from '@weareopenr/authkit';
import { FEATURE_FLAGS, ItemType, NO_INVENTORY_TYPES, REGULATORY_STATEMENTS } from 'src/constants';
import { getAllergens, getClaims } from 'src/helpers/recipe';
import { useItem } from 'src/hooks/useItem/useItem';
import { useInventoryItem } from 'src/hooks/useInventoryItem/useInventoryItem';
import { TMenuItemType } from 'src/types/menu.types';
import { hasRegulatoryStatement } from 'src/utils/regulatory-statements';
import { SpinnerContainer } from '../SpinnerContainer';
import { InfoTooltip, InfoTooltipType } from '../InfoTooltip/InfoTooltip';
import styles from './ProductPreviewTrayContent.module.scss';

const cx = classNames.bind(styles);

export const PRODUCT_TYPE_CHIPS: Record<TMenuItemType, { label: string; colour: ColourVariant }> = {
  STANDARD: { label: 'Standard', colour: 'blueberry' },
  MODIFIER: { label: 'Modifier', colour: 'kale' },
  BUNDLE: { label: 'Bundle', colour: 'grape' },
  'NON-FOOD': { label: 'Non-food', colour: 'honey' },
};

const yesNo = (value: boolean): string => (value ? 'Yes' : 'No');

const Section = ({ heading, children }: { heading: string; children: ReactNode }): ReactElement => (
  <section className={styles['section']}>
    <Typography size="large" weight="bold">
      {heading}
    </Typography>
    {children}
  </section>
);

const Field = ({
  heading,
  children,
  missing,
}: {
  heading: string;
  children: ReactNode;
  /** Renders a notifier dot against the heading, explaining that the value has not been set. */
  missing?: InfoTooltipType;
}): ReactElement => (
  <FormatInformation
    heading={heading}
    tooltip={missing ? <InfoTooltip type={missing} variant="notifier" /> : undefined}
  >
    <Typography size="small" className={styles['field-value']}>
      {children}
    </Typography>
  </FormatInformation>
);

export interface ProductPreviewTrayContentProps {
  itemId: string;
}

export const ProductPreviewTrayContent = ({ itemId }: ProductPreviewTrayContentProps): ReactElement | null => {
  const { data: item, isLoading } = useItem(itemId);
  const hasInventory = !!item && !NO_INVENTORY_TYPES.includes(item.type);
  // Skips the request entirely for types that never carry inventory, rather than
  // fetching a recipe the preview will not show and surfacing its error snackbar.
  const { data: inventoryItem } = useInventoryItem(hasInventory ? item.recipe?.product_id : undefined);
  const { featureFlags } = useAuthWrapper();

  // useItem throws when the product is missing, so an errored load leaves data undefined too.
  if (!item) {
    return isLoading ? (
      <SpinnerContainer data-testid="product-preview-loading">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    ) : null;
  }

  const typeChip = PRODUCT_TYPE_CHIPS[item.type];
  const rangeNames = item.ranges?.map(({ name }) => name).join(', ');
  const hasTaxRate = item.tax_rate !== null && item.tax_rate !== undefined;
  // The field is optional on the response, so an omitted one reads the same as an explicit null.
  const hasDepositFee = item.deposit_return_scheme !== null && item.deposit_return_scheme !== undefined;
  // Non-food products have no recipe in the design, even if one comes back on the item.
  const recipe = item.type === ItemType.NON_FOOD ? null : item.recipe;

  return (
    <div className={styles['container']}>
      <div className={styles['product']}>
        {item.image ? (
          <img className={cx('product-image')} src={item.image} alt={item.name} />
        ) : (
          <div
            className={cx('product-image', 'product-image-placeholder')}
            data-testid="product-preview-image-placeholder"
          >
            <SaladIcon size="large" />
          </div>
        )}
        <div className={styles['product-text']}>
          <Typography size="large" weight="bold">
            {item.name}
          </Typography>
          <Typography size="small">{item.consumerName || 'No consumer name'}</Typography>
        </div>
      </div>
      <hr className={styles['divider']} />
      <div className={styles['product-type']}>
        <Typography size="medium" weight="bold">
          Product Type
        </Typography>
        <Chip label={typeChip.label} backgroundColour={typeChip.colour} />
      </div>
      <hr className={styles['divider']} />
      <Section heading="Product Details">
        <Field heading="Description">{item.description || 'No description'}</Field>
        <Field heading="Product Range">{rangeNames || 'No range'}</Field>
        <Field heading="Default POS ID" missing={item.plu ? undefined : InfoTooltipType.MISSING_POS_ID}>
          {item.plu || '-'}
        </Field>
        {hasInventory && <Field heading="Contains Alcohol (> 0.5%)">{yesNo(!!inventoryItem?.contains_alcohol)}</Field>}
        {!hasInventory && <Field heading="Party Size">{item.party_size ?? '-'}</Field>}
        <Field heading="Age Verification Required?">
          {yesNo(hasRegulatoryStatement(item.regulatory_statements, REGULATORY_STATEMENTS.AGE_VERIFICATION.name))}
        </Field>
        {hasInventory && (
          <Field heading="Contains High Fat, Salt or Sugar">
            {yesNo(hasRegulatoryStatement(item.regulatory_statements, REGULATORY_STATEMENTS.HFSS.name))}
          </Field>
        )}
      </Section>
      <hr className={styles['divider']} />
      <Section heading="Tax & Deposit">
        <Field heading="Tax rate" missing={hasTaxRate ? undefined : InfoTooltipType.MISSING_TAX_RATE}>
          {hasTaxRate ? `${item.tax_rate}%` : '-'}
        </Field>
        {featureFlags.includes(FEATURE_FLAGS.DEPOSIT_RETURN_SCHEME) && (
          <Field heading="Deposit fee">
            {hasDepositFee ? item.deposit_return_scheme.toString() : 'No deposit fee'}
          </Field>
        )}
      </Section>
      {recipe && (
        <>
          <hr className={styles['divider']} />
          <Section heading="Recipe">
            <Field heading="Name">{recipe.name || '-'}</Field>
            <Field heading="Calories">{recipe.calories ?? '-'}</Field>
            <Field heading="Allergens">
              {getAllergens(recipe.allergens?.filter(({ type }) => type === 'KNOWN')) || 'None'}
            </Field>
            <Field heading="May Contain">
              {getAllergens(recipe.allergens?.filter(({ type }) => type === 'POTENTIAL')) || 'None'}
            </Field>
            <Field heading="Claims">{getClaims(recipe.claims) || 'No Claims'}</Field>
            <Field heading="Ingredient Declaration">{recipe.ingredient_declaration || '-'}</Field>
          </Section>
        </>
      )}
    </div>
  );
};

export default ProductPreviewTrayContent;
